import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const isDebugEnabled = () => {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('debug') === '1') return true;
    const ls = localStorage.getItem('qroke-debug');
    return ls === '1' || ls === 'true';
  } catch {
    return false;
  }
};

const DebugOverlay: React.FC = () => {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(isDebugEnabled());
  const [logs, setLogs] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled) return;
    const add = (msg: string) => {
      setLogs(prev => [...prev, msg].slice(-400));
      setTimeout(() => {
        try { containerRef.current?.scrollTo({ top: 999999, behavior: 'smooth' }); } catch {}
      }, 10);
    };

    // Seed with any preloaded logs captured before React mounted
    try {
      const pre: string[] = (window as any).__qrokeLogs || [];
      if (pre.length) {
        setLogs(prev => [...prev, ...pre].slice(-400));
      }
    } catch {}

    add('Debug overlay enabled');
    add(`${new Date().toISOString()} User: ${user ? user.uid : 'signed-out'}`);

    // Subscribe to live log events from the early logger
    const onQrokeLog = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (typeof detail === 'string') add(detail);
    };
    window.addEventListener('qroke-log', onQrokeLog as EventListener);

    return () => {
      window.removeEventListener('qroke-log', onQrokeLog as EventListener);
    };
  }, [enabled, user?.uid]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'd') {
        const next = !enabled;
        setEnabled(next);
        try { localStorage.setItem('qroke-debug', next ? '1' : '0'); } catch {}
      }
      if (!enabled) return null;

      const copyLogs = async () => {
        try {
          await navigator.clipboard.writeText(logs.join('\n'));
          alert('Logs copied to clipboard');
        } catch (e) {
          alert('Failed to copy logs');
        }
      };

      const downloadLogs = () => {
        try {
          const blob = new Blob([logs.join('\n')], { type: 'text/plain' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `qroke-debug-${new Date().toISOString().replace(/[:.]/g, '-')}.log`;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }, 0);
        } catch (e) {
          alert('Failed to download logs');
        }
      };
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);

  if (!enabled) return null;

  const copyLogs = async () => {
    try {
      await navigator.clipboard.writeText(logs.join('\n'));
      alert('Logs copied to clipboard');
    } catch (e) {
      alert('Failed to copy logs');
    }
  };

  const downloadLogs = () => {
    try {
      const blob = new Blob([logs.join('\n')], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qroke-debug-${new Date().toISOString().replace(/[:.]/g, '-')}.log`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);
    } catch (e) {
      alert('Failed to download logs');
    }
  };

  return (
    <div className="fixed bottom-16 left-2 right-2 md:bottom-4 md:left-auto md:right-4 z-[100]">
      <div className="bg-black/80 text-green-200 border border-green-600 rounded-lg shadow-xl">
        <div className="flex items-center justify-between px-3 py-2 border-b border-green-900">
          <div className="text-sm font-semibold">Debug (Ctrl/Cmd+Shift+D to toggle)</div>
          <div className="flex items-center gap-2">
            <button
              className="text-xs bg-green-700 hover:bg-green-600 text-white px-2 py-1 rounded"
              onClick={copyLogs}
            >
              Copy
            </button>
            <button
              className="text-xs bg-green-700 hover:bg-green-600 text-white px-2 py-1 rounded"
              onClick={downloadLogs}
            >
              Download
            </button>
            <button
              className="text-xs bg-green-700 hover:bg-green-600 text-white px-2 py-1 rounded"
              onClick={() => setLogs([])}
            >
              Clear
            </button>
            <button
              className="text-xs bg-green-700 hover:bg-green-600 text-white px-2 py-1 rounded"
              onClick={() => {
                try { localStorage.setItem('qroke-debug', '0'); } catch {}
                setEnabled(false);
              }}
            >
              Hide
            </button>
          </div>
        </div>
        <div ref={containerRef} className="max-h-48 overflow-auto p-2 text-xs whitespace-pre-wrap">
          {logs.length === 0 ? <div className="text-green-400">No logs yet…</div> : logs.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      </div>
    </div>
  );
};

export default DebugOverlay;
